/**
 * Onboarding spotlight overlay.
 *
 * Reads `highlightTargetId` from CanvasOnboardingContext, finds the DOM
 * node carrying `data-onboarding-target="<id>"`, and renders a pulsing
 * rust halo around it. Rendered via portal at document.body so the halo
 * sits above all canvas chrome regardless of where this component is
 * mounted in the tree.
 *
 * Position is recomputed on:
 *   • window resize / scroll
 *   • ResizeObserver on the target (e.g. layout shifts on the toolbar)
 *   • a microtask tick after `highlightTargetId` changes (lets the new
 *     target render before we measure)
 *
 * `pointer-events: none` on the halo so the user's click reaches the
 * underlying button — this is decoration, not an interactive layer.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useCanvasOnboardingState } from "../context/CanvasOnboardingContext";

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
  /**
   * Effective border-radius for the halo, in CSS pixels. Read from the
   * target's computed `border-top-left-radius` so the halo conforms to
   * round buttons (large radius → pill) and rectangular regions
   * (small/zero radius → softly-rounded box) without an explicit
   * shape-hint per target.
   */
  radius: number;
}

const HALO_PADDING = 8;
/**
 * Floor for the halo's corner radius. Targets with `border-radius: 0`
 * (most rectangular regions) still read as a softly-rounded halo
 * rather than a hard-edged box, which would feel out of place against
 * the rest of the onboarding's quiet pill aesthetic.
 */
const MIN_HALO_RADIUS = 14;

export function OnboardingSpotlight() {
  const { highlightTargetId, highlightEmphasisTick } =
    useCanvasOnboardingState();
  const [rect, setRect] = useState<Rect | null>(null);
  // One-shot emphasis pulse: when a step requests "look here!" by
  // bumping the tick, we apply the emphasis class for the duration of
  // the keyframe then remove it so the next bump can re-trigger.
  const [emphasizing, setEmphasizing] = useState(false);
  const lastTickRef = useRef(highlightEmphasisTick);
  useEffect(() => {
    if (highlightEmphasisTick === lastTickRef.current) return;
    lastTickRef.current = highlightEmphasisTick;
    setEmphasizing(true);
    const t = window.setTimeout(() => setEmphasizing(false), 1200);
    return () => window.clearTimeout(t);
  }, [highlightEmphasisTick]);

  useEffect(() => {
    if (!highlightTargetId) {
      setRect(null);
      return;
    }
    const selector = `[data-onboarding-target="${highlightTargetId}"]`;
    let el = document.querySelector<HTMLElement>(selector);

    // ResizeObserver is attached lazily inside `measure` — the target
    // may not be in the DOM when this effect first runs (e.g. the
    // toolbar is still animating in). `observedEl` tracks what the
    // observer is currently watching so we (re)bind it exactly once the
    // element appears, and rebind if the matching element changes.
    let observer: ResizeObserver | null = null;
    let observedEl: HTMLElement | null = null;

    const measure = () => {
      el = document.querySelector<HTMLElement>(selector);
      if (observer && el !== observedEl) {
        observer.disconnect();
        if (el) observer.observe(el);
        observedEl = el;
      }
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      // Pull the target's own corner radius so the halo conforms to
      // its silhouette. A pill-shaped button reports a huge radius
      // (clamped to half its short side by getBoundingClientRect's
      // own geometry); a rectangular region reports 0 or a small
      // value, which we floor to MIN_HALO_RADIUS to keep the halo
      // softly rounded rather than hard-edged.
      const computed = window.getComputedStyle(el);
      const parsed = parseFloat(computed.borderTopLeftRadius || "0") || 0;
      const maxRadius = Math.min(r.width, r.height) / 2 + HALO_PADDING;
      const radius = Math.min(
        Math.max(parsed + HALO_PADDING, MIN_HALO_RADIUS),
        maxRadius,
      );
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
        radius,
      });
    };

    // Create the observer up front; `measure` binds it to the target
    // as soon as the element exists (which may be after this effect's
    // first run).
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => measure());
    }

    // First measure on a microtask — the target may have just mounted.
    const initial = window.setTimeout(measure, 0);

    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    // Re-measure periodically while highlight is active to catch layout
    // shifts that don't fire any of the above (e.g. font load shifting
    // the toolbar). Cheap — one rAF every 250ms.
    let raf = 0;
    const tickStart = performance.now();
    const loop = (t: number) => {
      if (t - tickStart > 0 && (t - tickStart) % 250 < 16) measure();
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);

    return () => {
      window.clearTimeout(initial);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      observer?.disconnect();
      window.cancelAnimationFrame(raf);
    };
  }, [highlightTargetId]);

  if (!rect || !highlightTargetId) return null;

  return createPortal(
    <>
      <style>{ONBOARDING_SPOTLIGHT_STYLES}</style>
      <div
        aria-hidden
        className={
          emphasizing
            ? "_co_onb_spotlight _co_onb_spotlight_emphasis"
            : "_co_onb_spotlight"
        }
        style={{
          position: "fixed",
          top: rect.top - HALO_PADDING,
          left: rect.left - HALO_PADDING,
          width: rect.width + HALO_PADDING * 2,
          height: rect.height + HALO_PADDING * 2,
          borderRadius: rect.radius,
          pointerEvents: "none",
          zIndex: 60,
        }}
      />
    </>,
    document.body,
  );
}

/**
 * The halo: a 1.5px rust ring with a soft outer glow, breathing at 1.4s.
 * Color matches the rust eyebrow dot inside the onboarding widget so the
 * user reads "this is the same conversation, here's where to go next."
 */
const ONBOARDING_SPOTLIGHT_STYLES = `
  ._co_onb_spotlight {
    /* border-radius is set inline per-target so the halo matches the
       silhouette of pill buttons and rectangular regions alike. */
    box-shadow:
      0 0 0 1.5px #B86E49,
      0 0 0 6px rgba(184, 110, 73, 0.18),
      0 0 24px 4px rgba(184, 110, 73, 0.36);
    animation: _co_onb_spotlight_breathe 1.6s ease-in-out infinite;
    will-change: opacity, transform;
  }
  @keyframes _co_onb_spotlight_breathe {
    0%, 100% { opacity: 0.85; transform: scale(1);    }
    50%      { opacity: 1;    transform: scale(1.04); }
  }
  /* One-shot emphasis pulse — bigger amplitude, thicker outer ring,
     overrides the steady breathe for ~900ms. Triggered when a step
     wants to draw the user's eye back to the spotlight (e.g. they
     clicked the in-card instruction instead of the highlighted
     element). */
  @keyframes _co_onb_spotlight_emphasis {
    0% {
      opacity: 1;
      transform: scale(1);
      box-shadow:
        0 0 0 1.5px #B86E49,
        0 0 0 6px rgba(184, 110, 73, 0.18),
        0 0 24px 4px rgba(184, 110, 73, 0.36);
    }
    35% {
      opacity: 1;
      transform: scale(1.55);
      box-shadow:
        0 0 0 5px #B86E49,
        0 0 0 28px rgba(184, 110, 73, 0.32),
        0 0 100px 32px rgba(184, 110, 73, 0.7);
    }
    55% {
      opacity: 1;
      transform: scale(1.35);
      box-shadow:
        0 0 0 4px #B86E49,
        0 0 0 22px rgba(184, 110, 73, 0.26),
        0 0 80px 22px rgba(184, 110, 73, 0.55);
    }
    100% {
      opacity: 0.85;
      transform: scale(1);
      box-shadow:
        0 0 0 1.5px #B86E49,
        0 0 0 6px rgba(184, 110, 73, 0.18),
        0 0 24px 4px rgba(184, 110, 73, 0.36);
    }
  }
  ._co_onb_spotlight_emphasis {
    animation: _co_onb_spotlight_emphasis 1200ms cubic-bezier(0.2, 0, 0, 1) !important;
  }
  @media (prefers-reduced-motion: reduce) {
    ._co_onb_spotlight,
    ._co_onb_spotlight_emphasis { animation: none; opacity: 1; }
  }
`;
